import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import {
  Box, CircularProgress, Container, ThemeProvider, Typography, useTheme,
} from '@material-ui/core';
import { usePostHog, useFeatureFlagVariantKey } from 'posthog-js/react';
import { isValidPhoneNumber } from 'react-phone-number-input';

import sparkTheme from '@/themes/sparkTheme';
import Button from '@/components/Button';
import { TextField } from '@/components';
import { Header } from '@/pages/Onboarding/components/Header/Header';
import { Footer } from '@/pages/Onboarding/components/Footer/Footer';
import { Titles } from '@/pages/Onboarding/components/Titles/Titles';
import { ListBoxWithIcons } from '@/pages/Onboarding/components/ListBoxWithIcons/ListBoxWithIcons';
import AnswerQuestionsIcon from '@/assets/AnswerQuestionsIcon.svg';
import DownloadTheAppIcon from '@/assets/DownloadTheAppIcon.svg';
import BenefitsOfSparkIcon from '@/assets/BenefitsOfSparkIcon.svg';
import { ConsentWidget } from '@/pages/Onboarding/components/ConsentWidget/ConsentWidget';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';
// eslint-disable-next-line max-len
import { TermsAndConditionsAcknowledgements } from '@/pages/Onboarding/components/TermsAndConditionsAcknowledgements/TermsAndConditionsAcknowledgements';
import { DataSharingInfo } from '@/pages/Onboarding/components/DataSharingInfo/DataSharingInfo';
// eslint-disable-next-line max-len
import { WhyDoWeNeedThisInfo } from '@/pages/Onboarding/components/OnboardingPopover/popoverContent/WhyDoWeNeedThisInfo';
import { useConsent } from '@/hooks/useConsent';
import { useAcceptedLanguage } from '@/hooks/useAcceptedLanguage';
import {
  Product, getPdtUid, getProductId, getProductMinAge, getLob,
} from '@/utils/productHelpers';
import {
  validateZipCode, validateEmployeeId, getAge, validateEmail, isValidFormattedDob,
} from '@/utils/stringValidators';
import { formatDateString } from '@/utils/stringUtils';
import { formatDateInput } from '@/utils/inputHelpers';
import { ELIGIBILITY_CONFIGURATION_QUERY, CHECK_ELIGIBILITY_QUERY } from '@/apollo/queries';
import { ENROLL_PATIENT } from '@/apollo/mutations';
import { activateEmail, activateSparkPhone } from '@/utils/sparkActivationApi';
import { EMPLOYER_ORG_SLUG_KEY, EN } from '@/utils/constants';
import { ActiveFeatureFlag } from '@/hooks/usePostHogFeatureFlags';

type EligibleData = {
  firstName: string;
  lastName: string;
  dateOfBirth: string;
  postalCode?: string;
  employeeId?: string;
};

const DependentEligibilityCheckInner: React.FC = () => {
  useAcceptedLanguage();
  useConsent();

  const history = useHistory();
  const posthog = usePostHog();
  const { product, org_slug: orgSlug } = useParams<{ product: Product; org_slug: string }>();
  const { t, i18n } = useTranslation();

  // Employer flow guard: block direct URL access
  const [isGuardPassed, setIsGuardPassed] = useState(false);
  const sparkAuthFlag = useFeatureFlagVariantKey(ActiveFeatureFlag.SPARK_AUTH);

  useEffect(() => {
    const storedSlug = sessionStorage.getItem(EMPLOYER_ORG_SLUG_KEY);
    if (storedSlug !== orgSlug) {
      sessionStorage.setItem(EMPLOYER_ORG_SLUG_KEY, orgSlug);
    }

    if (sparkAuthFlag === undefined) { return; }
    if (sparkAuthFlag !== 'enroll_type') {
      history.replace('/activate/spark/employers');
      return;
    }

    setIsGuardPassed(true);
  }, [orgSlug, history, sparkAuthFlag]);

  // Fetch org eligibility config — deferred until guard passes to avoid firing on blocked visits
  const { data: configData, loading: configLoading } = useQuery(ELIGIBILITY_CONFIGURATION_QUERY, {
    variables: { organizationSlug: orgSlug, pdtUid: getPdtUid(product) },
    skip: !orgSlug || !isGuardPassed,
  });
  const config = configData?.eligibilityConfiguration;
  const showZipCode = config?.collectZipcodeForEligibility ?? false;
  const showEmployeeId = config?.hasEmployeeId ?? false;
  const employeeIdLabel = config?.employeeIdFieldRename
    || t('activate.employers.dependent_eligibility.employee_id_label');
  const minAge = getProductMinAge(product);

  // Step state
  const [step, setStep] = useState<'eligibility' | 'covered' | 'contact' | 'caregiver'>('eligibility');
  const [eligibleData, setEligibleData] = useState<EligibleData | null>(null);

  // Step 1 — form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [dob, setDob] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [employeeId, setEmployeeId] = useState('');

  // Step 1 — error state
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [dobError, setDobError] = useState('');
  const [zipCodeError, setZipCodeError] = useState(false);
  const [employeeIdError, setEmployeeIdError] = useState('');
  const [generalError, setGeneralError] = useState('');
  const [isIneligible, setIsIneligible] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Step 3 — contact form state
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailError, setEmailError] = useState(false);
  const [phoneError, setPhoneError] = useState(false);

  // Step 4 — caregiver form state
  const [cgFirstName, setCgFirstName] = useState('');
  const [cgLastName, setCgLastName] = useState('');
  const [cgEmail, setCgEmail] = useState('');
  const [cgZipCode, setCgZipCode] = useState('');
  const [cgDob, setCgDob] = useState('');
  const [cgTerms, setCgTerms] = useState(false);
  const [cgFirstNameError, setCgFirstNameError] = useState(false);
  const [cgLastNameError, setCgLastNameError] = useState(false);
  const [cgEmailError, setCgEmailError] = useState(false);
  const [cgZipError, setCgZipError] = useState(false);
  const [cgDobError, setCgDobError] = useState('');
  const [cgGeneralError, setCgGeneralError] = useState('');
  const [isCgSubmitting, setIsCgSubmitting] = useState(false);
  const [cgPopoverOpen, setCgPopoverOpen] = useState(false);
  const [enrolledUserUid, setEnrolledUserUid] = useState<string | null>(null);

  const theme = useTheme();
  const [checkEligibility] = useLazyQuery(CHECK_ELIGIBILITY_QUERY, { fetchPolicy: 'no-cache' });
  const [enrollPatient] = useMutation(ENROLL_PATIENT, { errorPolicy: 'all' });

  const isDobValid = isValidFormattedDob(dob) && getAge(dob) >= minAge;

  const isEmployeeIdValid = !showEmployeeId || validateEmployeeId(employeeId.trim(), {
    validationEnabled: config?.employeeIdValidationEnabled,
    allowedChars: config?.employeeIdAllowedChars,
    minLength: config?.employeeIdMinLength,
    maxLength: config?.employeeIdMaxLength,
    errorMessage: config?.employeeIdValidationErrorMessage,
  }).isValid;

  const isEligibilityFormValid = firstName.trim() !== ''
    && lastName.trim() !== ''
    && isDobValid
    && (showZipCode ? validateZipCode(zipCode) : true)
    && isEmployeeIdValid;

  const isContactFormValid = validateEmail(email) && isValidPhoneNumber(phone);

  const isCgDobValid = isValidFormattedDob(cgDob);
  const cgEmailSameAsDep = validateEmail(cgEmail)
    && cgEmail.trim().toLowerCase() === email.trim().toLowerCase();
  let cgEmailHelperText = '';
  if (cgEmailError) {
    cgEmailHelperText = t('coverage.errors.email_error');
  } else if (cgEmailSameAsDep) {
    cgEmailHelperText = t('coverage.spark.caregiver.caregiver_email_same_error');
  }
  const isCgFormValid = cgFirstName.trim() !== ''
    && cgLastName.trim() !== ''
    && validateEmail(cgEmail)
    && cgEmail.trim().toLowerCase() !== email.trim().toLowerCase()
    && validateZipCode(cgZipCode)
    && isCgDobValid
    && cgTerms;

  const handleEligibilitySubmit = async () => {
    setGeneralError('');

    try {
      setIsSubmitting(true);

      const eligibilityResult = await checkEligibility({
        variables: {
          pdtUid: getPdtUid(product),
          firstName: firstName.trim(),
          lastName: lastName.trim(),
          dateOfBirth: formatDateString(dob),
          organizationSlug: orgSlug,
          productId: getProductId(product),
          postalCode: showZipCode ? zipCode : undefined,
          employeeId: showEmployeeId ? employeeId.trim() : undefined,
        },
      });

      if (eligibilityResult?.data?.eligibilityStatus?.isEligible === false) {
        setIsIneligible(true);
        setIsSubmitting(false);
        return;
      }

      if (!eligibilityResult?.data?.eligibilityStatus?.isEligible) {
        setGeneralError(t('coverage.errors.generic_error'));
        setIsSubmitting(false);
        return;
      }

      posthog?.capture('DependentEligibilityCheckPassed', { organizationSlug: orgSlug });

      setEligibleData({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        dateOfBirth: formatDateString(dob),
        postalCode: showZipCode ? zipCode : undefined,
        employeeId: showEmployeeId ? employeeId.trim() : undefined,
      });
      setStep('covered');
      setIsSubmitting(false);
    } catch (err) {
      console.error('[DependentEligibilityCheck] submit failed:', err);
      setGeneralError(t('coverage.errors.generic_error'));
      setIsSubmitting(false);
    }
  };

  const handleCaregiverSubmit = async () => {
    setCgGeneralError('');
    try {
      setIsCgSubmitting(true);

      let userUid: string;

      if (enrolledUserUid) {
        userUid = enrolledUserUid;
      } else {
        const result = await enrollPatient({
          variables: {
            email: email.trim(),
            phone: phone || null,
            firstName: eligibleData?.firstName,
            lastName: eligibleData?.lastName,
            dateOfBirth: eligibleData?.dateOfBirth,
            postalCode: eligibleData?.postalCode,
            externalId: eligibleData?.employeeId,
            organizationSlug: orgSlug,
            pdtUid: getPdtUid(product),
            language: i18n.language.toUpperCase().split('-')[0] || EN,
            termsAndPrivacyAcknowledgedAt: new Date().toISOString(),
            emergencyContactName: `${cgFirstName.trim()} ${cgLastName.trim()}`,
            emergencyContactEmail: cgEmail.trim(),
            isDependent: true,
          },
        });

        const newUid = result?.data?.enrollDirectAccessPatient?.userUid;

        if (!newUid) {
          console.error('[DependentEligibilityCheck] enrollment failed — no userUid', result?.errors);
          setCgGeneralError(t('coverage.errors.generic_error'));
          return;
        }

        setEnrolledUserUid(newUid);
        userUid = newUid;
      }

      const phoneActivationResult = await activateSparkPhone({
        phone,
        organizationSlug: orgSlug,
        lob: getLob(orgSlug),
        limbixUserId: userUid,
        isDependent: true,
        phDistinctId: posthog?.get_distinct_id(),
      });
      // TODO(SD-2238): remove this temporary debug log once we confirm the attach response body
      console.log('[DEBUG SD-2238] activateSparkPhone response:', phoneActivationResult);

      const emailActivationResult = await activateEmail({
        email: email.trim(),
        product,
        organizationSlug: orgSlug,
        limbixUserId: userUid,
        lob: getLob(orgSlug),
        isDependent: true,
      });
      // TODO(SD-2238): remove this temporary debug log once we confirm the attach response body
      console.log('[DEBUG SD-2238] activateEmail response:', emailActivationResult);
      history.push(`/activate/${product}/check-email`, {
        email: email.trim(),
        organizationSlug: orgSlug,
        limbixUserId: userUid,
        isDependent: true,
      });
    } catch (err) {
      console.error('[DependentEligibilityCheck] caregiver submit failed:', err);
      setCgGeneralError(t('coverage.errors.generic_error'));
    } finally {
      setIsCgSubmitting(false);
    }
  };

  if (isIneligible) {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Container maxWidth="xs" style={{ flex: 1, paddingTop: 32 }}>
          <Typography variant="h3" style={{ marginBottom: 8 }}>
            {t('activate.employers.dependent_eligibility.ineligible_title')}
          </Typography>
          <Typography style={{ marginBottom: 8 }}>
            {t('activate.employers.dependent_eligibility.ineligible_body')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            rounded
            style={{ borderRadius: 100, marginTop: 16 }}
            onClick={() => setIsIneligible(false)}
          >
            {t('activate.employers.dependent_eligibility.retry')}
          </Button>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (step === 'covered') {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <Titles
            title={t('coverage.spark.caregiver.coverage_confirmation.title')}
            subtitle={t('coverage.spark.caregiver.coverage_confirmation.subtitle')}
          />
          <ListBoxWithIcons
            title={t('coverage.spark.caregiver.coverage_confirmation.list_title')}
            listItems={[
              {
                icon: AnswerQuestionsIcon,
                listText: t('coverage.spark.caregiver.coverage_confirmation.list_item0'),
              },
              {
                icon: DownloadTheAppIcon,
                listText: t('coverage.spark.caregiver.coverage_confirmation.list_item1'),
              },
              {
                icon: BenefitsOfSparkIcon,
                listText: t('coverage.spark.caregiver.coverage_confirmation.list_item2'),
              },
            ]}
          />
          <Box sx={{ width: '100%', justifyItems: 'center' }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              rounded
              style={{ borderRadius: 100 }}
              onClick={() => setStep('contact')}
            >
              {t('coverage.next')}
            </Button>
          </Box>
        </Container>
        <Footer />
      </Box>
    );
  }

  if (step === 'contact') {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <Box style={{ marginTop: 32, marginBottom: 16 }}>
            <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
              {t('coverage.spark.caregiver.patient_title')}
            </Typography>
            <Typography color="textPrimary">
              <u>{t('coverage.spark.caregiver.patient_subtitle')}</u>
            </Typography>
          </Box>

          <Typography variant="h2" color="textPrimary">
            {t('coverage.spark.caregiver.patient_email_title')}
          </Typography>
          <Typography color="textPrimary" style={{ marginBottom: 8 }}>
            {t('coverage.spark.caregiver.patient_email_subtitle_activation')}
          </Typography>

          <Box style={{ marginBottom: 32 }}>
            <TextField
              required
              error={emailError}
              helperText={emailError ? t('coverage.errors.email_error') : ''}
              label={t('coverage.spark.caregiver.email_label')}
              id="dependentEmail"
              inputProps={{
                placeholder: t('coverage.spark.caregiver.email_label'),
                'data-testid': 'dependent-email-input',
              }}
              value={email}
              onChange={(e) => {
                setEmailError(false);
                setEmail(e.target.value);
              }}
              onBlur={(e) => {
                setEmailError(!validateEmail(e.target.value));
              }}
            />
            <PhoneNumberInput
              placeholder={t('coverage.spark.caregiver.phone_label')}
              value={phone}
              id="dependentPhone"
              onChange={(value) => {
                setPhoneError(false);
                setPhone(value || '');
              }}
              onBlur={() => {
                setPhoneError(!isValidPhoneNumber(phone));
              }}
              error={phoneError}
              helperText={phoneError ? t('coverage.errors.phone_error') : ''}
            />

          </Box>

          <Button
            variant="contained"
            color="primary"
            fullWidth
            rounded
            type="button"
            data-testid="dependent-contact-submit"
            disabled={!isContactFormValid}
            onClick={() => setStep('caregiver')}
            style={{ borderRadius: 100 }}
          >
            {t('coverage.next')}
          </Button>

          <ConsentWidget />
        </Container>
        <Footer />
      </Box>
    );
  }

  if (step === 'caregiver') {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Container maxWidth="xs" style={{ flex: 1 }}>
          <Titles
            title={t('coverage.spark.caregiver.caregiver_title')}
          />
          <Box
            onClick={() => setCgPopoverOpen(!cgPopoverOpen)}
            style={{ cursor: 'pointer', marginBottom: 8 }}
          >
            <Typography>
              <u>{t('coverage.spark.caregiver.caregiver_subtitle')}</u>
            </Typography>
          </Box>
          <Typography variant="h2" style={{ color: theme.palette.grey[800] }}>
            {t('coverage.spark.caregiver.caregiver_info_title')}
          </Typography>

          <TextField
            required
            error={cgFirstNameError}
            helperText={cgFirstNameError ? t('coverage.errors.first_name_error') : ''}
            label={t('coverage.spark.caregiver.caregiver_f_name_label')}
            id="cgFirstName"
            inputProps={{ placeholder: t('coverage.spark.caregiver.caregiver_f_name_label') }}
            value={cgFirstName}
            onChange={(e) => {
              setCgFirstNameError(false);
              setCgFirstName(e.target.value);
            }}
            onBlur={(e) => setCgFirstNameError(e.target.value.trim() === '')}
          />
          <TextField
            required
            error={cgLastNameError}
            helperText={cgLastNameError ? t('coverage.errors.last_name_error') : ''}
            label={t('coverage.spark.caregiver.caregiver_l_name_label')}
            id="cgLastName"
            inputProps={{ placeholder: t('coverage.spark.caregiver.caregiver_l_name_label') }}
            value={cgLastName}
            onChange={(e) => {
              setCgLastNameError(false);
              setCgLastName(e.target.value);
            }}
            onBlur={(e) => setCgLastNameError(e.target.value.trim() === '')}
          />
          <TextField
            required
            error={cgEmailError || cgEmailSameAsDep}
            helperText={cgEmailHelperText}
            label={t('coverage.spark.caregiver.caregiver_email_label')}
            id="cgEmail"
            inputProps={{ placeholder: t('coverage.spark.caregiver.caregiver_email_label') }}
            value={cgEmail}
            onChange={(e) => {
              setCgEmailError(false);
              setCgEmail(e.target.value);
            }}
            onBlur={(e) => setCgEmailError(!validateEmail(e.target.value))}
          />
          <TextField
            required
            error={cgZipError}
            helperText={cgZipError ? t('coverage.errors.zipcode_error') : ''}
            label={t('coverage.spark.caregiver.caregiver_zipcode_label')}
            id="cgZipCode"
            inputProps={{ placeholder: t('coverage.spark.caregiver.caregiver_zipcode_label') }}
            value={cgZipCode}
            onChange={(e) => {
              setCgZipError(false);
              setCgZipCode(e.target.value);
            }}
            onBlur={(e) => setCgZipError(!validateZipCode(e.target.value))}
          />
          <TextField
            required
            error={!!cgDobError}
            helperText={cgDobError}
            label={t('coverage.spark.caregiver.caregiver_dob_label')}
            id="cgDob"
            inputProps={{
              placeholder: 'MM / DD / YYYY',
              maxLength: 14,
              inputMode: 'numeric' as const,
            }}
            value={formatDateInput(cgDob)}
            onChange={(e) => {
              setCgDobError('');
              setCgDob(formatDateInput(e.target.value));
            }}
            onBlur={() => {
              if (cgDob && !isValidFormattedDob(cgDob)) {
                setCgDobError(t('coverage.errors.dob_error'));
              }
            }}
          />

          <div style={{ marginBottom: '32px' }} />
          <TermsAndConditionsAcknowledgements
            safetyAcknowledgement
            safetyAcknowledgementLabel={t('coverage.safety_acknowledgement_label')}
            policyAndTermsAcknowledgement={cgTerms}
            onSelect={(data) => {
              if ('policyAndTermsAcknowledgement' in data) {
                setCgTerms(data.policyAndTermsAcknowledgement);
              }
            }}
          />

          {cgGeneralError && (
            <Typography color="error" style={{ marginTop: 8 }}>
              {cgGeneralError}
            </Typography>
          )}

          <Box sx={{ width: '100%', justifyItems: 'center', marginTop: 24 }}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              rounded
              type="button"
              data-testid="caregiver-submit"
              disabled={!isCgFormValid || isCgSubmitting}
              onClick={handleCaregiverSubmit}
              loading={isCgSubmitting}
              style={{ borderRadius: 100 }}
            >
              {t('coverage.next')}
            </Button>
          </Box>
          <DataSharingInfo />
          <WhyDoWeNeedThisInfo
            personEnrolling="caregiver"
            popoverOpen={cgPopoverOpen}
            setPopoverOpen={setCgPopoverOpen}
          />
        </Container>
        <Footer />
      </Box>
    );
  }

  if (configLoading) {
    return (
      <Box display="flex" flexDirection="column" minHeight="100vh">
        <Header />
        <Box display="flex" justifyContent="center" alignItems="center" style={{ flex: 1 }}>
          <CircularProgress color="primary" />
        </Box>
        <Footer />
      </Box>
    );
  }

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh">
      <Header />
      <Container maxWidth="xs" style={{ flex: 1 }}>
        <Box style={{ marginTop: 32, marginBottom: 16 }}>
          <Typography variant="h2" style={{ marginBottom: 8 }} color="textPrimary">
            {t('coverage.spark.caregiver.coverage_title')}
          </Typography>
          <Typography color="textPrimary">
            {t('coverage.spark.caregiver.coverage_subtitle')}
          </Typography>
        </Box>

        <Titles
          title={t('coverage.spark.caregiver.coverage_form_title')}
        />
        <Box style={{ marginBottom: 32 }}>
          <TextField
            required
            error={firstNameError}
            helperText={firstNameError ? t('coverage.errors.first_name_error') : ''}
            label={t('activate.employers.dependent_eligibility.f_name_label')}
            inputProps={{
              placeholder: t('activate.employers.dependent_eligibility.f_name_label'),
              'data-testid': 'dependent-first-name-input',
            }}
            value={firstName}
            onChange={(e) => {
              setFirstNameError(false);
              setFirstName(e.target.value);
            }}
            onBlur={(e) => setFirstNameError(e.target.value.trim() === '')}
          />
          <TextField
            required
            error={lastNameError}
            helperText={lastNameError ? t('coverage.errors.last_name_error') : ''}
            label={t('activate.employers.dependent_eligibility.l_name_label')}
            inputProps={{
              placeholder: t('activate.employers.dependent_eligibility.l_name_label'),
              'data-testid': 'dependent-last-name-input',
            }}
            value={lastName}
            onChange={(e) => {
              setLastNameError(false);
              setLastName(e.target.value);
            }}
            onBlur={(e) => setLastNameError(e.target.value.trim() === '')}
          />
          {showZipCode && (
            <TextField
              required
              error={zipCodeError}
              helperText={zipCodeError ? t('coverage.errors.zipcode_error') : ''}
              label={t('activate.employers.dependent_eligibility.zipcode_label')}
              inputProps={{
                placeholder: t('activate.employers.dependent_eligibility.zipcode_label'),
                'data-testid': 'dependent-zipcode-input',
              }}
              value={zipCode}
              onChange={(e) => {
                setZipCodeError(false);
                setZipCode(e.target.value);
              }}
              onBlur={(e) => setZipCodeError(!validateZipCode(e.target.value))}
            />
          )}
          {showEmployeeId && (
            <TextField
              required
              error={!!employeeIdError}
              helperText={employeeIdError}
              label={employeeIdLabel}
              inputProps={{
                placeholder: config?.employeeIdExample || employeeIdLabel,
                'data-testid': 'dependent-employee-id-input',
              }}
              value={employeeId}
              onChange={(e) => {
                setEmployeeIdError('');
                setEmployeeId(e.target.value);
              }}
              onBlur={(e) => {
                const result = validateEmployeeId(e.target.value.trim(), {
                  validationEnabled: config?.employeeIdValidationEnabled,
                  allowedChars: config?.employeeIdAllowedChars,
                  minLength: config?.employeeIdMinLength,
                  maxLength: config?.employeeIdMaxLength,
                  errorMessage: config?.employeeIdValidationErrorMessage,
                });
                if (!result.isValid) {
                  setEmployeeIdError(result.errorMessage || t('coverage.errors.employee_id_error'));
                }
              }}
            />
          )}
          <TextField
            required
            error={!!dobError}
            helperText={dobError}
            label={t('activate.employers.dependent_eligibility.dob_label')}
            inputProps={{
              placeholder: 'MM / DD / YYYY',
              maxLength: 14,
              inputMode: 'numeric' as const,
              'data-testid': 'dependent-dob-input',
            }}
            value={formatDateInput(dob)}
            onChange={(e) => {
              setDobError('');
              setDob(formatDateInput(e.target.value));
            }}
            onBlur={() => {
              if (!dob) { return; }
              if (!isValidFormattedDob(dob)) {
                setDobError(t('coverage.errors.dob_error'));
              } else if (getAge(dob) < minAge) {
                setDobError(t('coverage.spark.caregiver.dob_age_error'));
              }
            }}
          />

          {generalError && (
            <Typography color="error" style={{ marginTop: 8 }} data-testid="general-error">
              {generalError}
            </Typography>
          )}
        </Box>

        <Button
          variant="contained"
          color="primary"
          fullWidth
          rounded
          type="button"
          data-testid="dependent-eligibility-submit"
          disabled={!isEligibilityFormValid || isSubmitting}
          onClick={handleEligibilitySubmit}
          loading={isSubmitting}
          style={{ borderRadius: 100 }}
        >
          {isSubmitting
            ? t('activate.employers.dependent_eligibility.checking')
            : t('activate.employers.dependent_eligibility.continue')}
        </Button>

        <ConsentWidget />
      </Container>
      <Footer />
    </Box>
  );
};

const DependentEligibilityCheck: React.FC = () => (
  <ThemeProvider theme={sparkTheme}>
    <DependentEligibilityCheckInner />
  </ThemeProvider>
);

export default DependentEligibilityCheck;
